import { useMemo, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Archive, BrainCircuit, Save } from "lucide-react";
import { buildClinicalIntelligenceSummary } from "@/domain/clinicalIntelligenceEngine";
import { buildLocalClinicalGuidance } from "@/domain/localClinicalGuidanceEngine";
import { classifyRepeatDoses } from "@/domain/doseOverlapClassifier";
import { interpretClinicalTexts } from "@/domain/clinicalTextInterpreter";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/lib/auth";
import { toast } from "@/lib/safeToast";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";

const SOURCE = "clinicalos-local-integrated-v2";

export function ClinicalAnalysisSnapshotPanel({
  patient,
  medications = [],
  doses = [],
  checkins = [],
  sessions = [],
  substanceUse = [],
  inventory = [],
  targetSymptoms = [],
  symptomMeasurements = [],
  periodDays = 30,
}: {
  patient?: any;
  medications?: any[];
  doses?: any[];
  checkins?: any[];
  sessions?: any[];
  substanceUse?: any[];
  inventory?: any[];
  targetSymptoms?: any[];
  symptomMeasurements?: any[];
  periodDays?: number;
}) {
  const { user } = useAuth();
  const qc = useQueryClient();
  const [saving, setSaving] = useState(false);
  const patientId = patient?.id;

  const summary = useMemo(() => buildClinicalIntelligenceSummary({
    patient, medications, doses, checkins, sessions, substanceUse, inventory, periodDays, targetSymptoms, symptomMeasurements,
  }), [patient, medications, doses, checkins, sessions, substanceUse, inventory, periodDays, targetSymptoms, symptomMeasurements]);

  const guidance = useMemo(() => buildLocalClinicalGuidance({
    patient, medications, doses, checkins, sessions, substanceUse, inventory, periodDays, targetSymptoms, symptomMeasurements,
  }), [patient, medications, doses, checkins, sessions, substanceUse, inventory, periodDays, targetSymptoms, symptomMeasurements]);

  const repeatDoseSignals = useMemo(() => classifyRepeatDoses(doses), [doses]);
  const textInterpretation = useMemo(() => interpretClinicalTexts({ patient, sessions, checkins, doses }), [patient, sessions, checkins, doses]);

  const { data: latestSnapshot } = useQuery({
    queryKey: ["clinical-analysis-snapshot", patientId],
    enabled: !!patientId,
    queryFn: async () => {
      const { data, error } = await (supabase as any)
        .from("clinical_ai_analyses")
        .select("id, source, payload, created_at")
        .eq("patient_id", patientId)
        .in("source", [SOURCE, "clinicalos-local-integrated-v1"])
        .order("created_at", { ascending: false })
        .limit(1)
        .maybeSingle();
      if (error) throw error;
      return data;
    },
  });

  async function saveSnapshot() {
    if (!user?.id || !patientId) {
      toast.error("Selecione um paciente e confirme a sessão.");
      return;
    }
    setSaving(true);
    try {
      const generatedAt = new Date().toISOString();
      const payload = {
        schema_version: 2,
        generated_at: generatedAt,
        engine: SOURCE,
        period_days: periodDays,
        disclaimer: "Apoio à revisão médica. Hipóteses e associações temporais não equivalem a diagnóstico, causalidade ou prescrição.",
        patient_snapshot: {
          id: patient.id,
          name: patient.full_name,
          primary_diagnoses: patient.primary_diagnoses ?? [],
          current_complaint: patient.current_complaint ?? null,
          clinical_history_present: Boolean(patient.clinical_history),
        },
        source_inventory: {
          medications: medications.length,
          doses: doses.length,
          checkins: checkins.length,
          sessions: sessions.length,
          substance_use_events: substanceUse.length,
          target_symptoms: targetSymptoms.length,
          symptom_measurements: symptomMeasurements.length,
          clinical_text_sources: textInterpretation.sourcesRead.map((source) => ({
            id: source.id,
            source_type: source.sourceType,
            field: source.field,
            label: source.label,
            at: source.at ?? null,
          })),
        },
        clinical_intelligence: summary,
        local_guidance: guidance,
        clinical_text_interpretation: textInterpretation,
        repeat_dose_analysis: repeatDoseSignals,
        review_state: {
          status: "pendente de revisão médica",
          reviewed_by: null,
          reviewed_at: null,
        },
      };

      const { data, error } = await (supabase as any)
        .from("clinical_ai_analyses")
        .insert({ owner_id: user.id, patient_id: patientId, session_id: null, source: SOURCE, payload })
        .select("id, created_at")
        .single();
      if (error) throw error;
      await qc.invalidateQueries({ queryKey: ["clinical-analysis-snapshot", patientId] });
      toast.success("Snapshot clínico v2 salvo com textos, hipóteses e fontes para revisão médica.");
      console.info("[ClinicalAnalysisSnapshot] saved", { id: data?.id, patientId, textSources: textInterpretation.sourcesRead.length });
    } catch (error: any) {
      console.error("[ClinicalAnalysisSnapshot] save failed", error);
      toast.error(error?.message ?? "Falha ao salvar análise clínica.");
    } finally {
      setSaving(false);
    }
  }

  const latestAt = latestSnapshot?.created_at
    ? new Intl.DateTimeFormat("pt-BR", { dateStyle: "short", timeStyle: "short" }).format(new Date(latestSnapshot.created_at))
    : null;
  const positiveTextSignals = textInterpretation.signals.filter((signal) => signal.positiveEvidence.length).length;

  return (
    <Card className="min-w-0 space-y-3 overflow-hidden border-primary/20 bg-primary/5 p-4">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div className="min-w-0">
          <div className="flex items-center gap-2 font-medium">
            <BrainCircuit className="h-4 w-4 text-primary" /> Snapshot da inteligência clínica
          </div>
          <p className="mt-1 text-xs leading-relaxed text-muted-foreground">
            Congela os dados, fontes textuais, hipóteses, negações, contradições, lacunas, estratégias e análise farmacológica daquele momento. Fica pendente de revisão médica.
          </p>
        </div>
        <Button onClick={saveSnapshot} disabled={saving || !patientId} className="w-full shrink-0 sm:w-auto">
          <Save className="mr-2 h-4 w-4" />{saving ? "Salvando…" : "Salvar análise no prontuário"}
        </Button>
      </div>

      <div className="flex flex-wrap items-center gap-2 text-xs">
        <Badge variant="secondary">risco {summary.riskLabel}</Badge>
        <Badge variant="outline">qualidade {summary.dataQualityScore}/100</Badge>
        <Badge variant="outline">{summary.actionCards.length} ação(ões)</Badge>
        <Badge variant="outline">{positiveTextSignals} sinal(is) textual(is)</Badge>
        <Badge variant="outline">{repeatDoseSignals.filter((signal) => signal.kind !== "separate-episode").length} sobreposição(ões)</Badge>
      </div>

      <div className="flex items-start gap-2 rounded-xl border bg-background/60 p-3 text-xs text-muted-foreground">
        <Archive className="mt-0.5 h-4 w-4 shrink-0" />
        <span>{latestAt ? `Último snapshot salvo em ${latestAt}.` : "Ainda não há snapshot persistido para este paciente."} Cada salvamento cria uma versão histórica; não sobrescreve as anteriores.</span>
      </div>
    </Card>
  );
}
